import React from 'react';
import { Col, Row } from 'reactstrap';
import { FormFieldHeader } from '../../../../../../../components/FormFieldHeader';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  useFormState,
} from 'react-hook-form';
import { PushToHrisFormValues } from '../../..';
import { SelectOption } from '../../../../../../../components/Select';
import { HrisDepartmentService } from '../../../../../../../services/v1/applicant_tracking/HrisDepartmentService';
import { HrisJobTitleService } from '../../../../../../../services/v1/applicant_tracking/HrisJobTitleService';
import { CreatablePaginatedSelect } from '../../../../../../../components/Select/CreatablePaginatedSelect';
import { hrisFieldWarningTooltip, isFreeText } from '../../../hrisFieldWarning';

interface PropTypes {
  register: UseFormRegister<PushToHrisFormValues>;
  errors: FieldErrors;
  control: Control<PushToHrisFormValues>;
  setValue: UseFormSetValue<PushToHrisFormValues>;
  hrisProviderLabel: string;
  jobTitleSuggestionLabel: string | null;
  departmentSuggestionLabel: string | null;
}

interface AdditionalType {
  page: number;
}

const defaultAdditional: AdditionalType = {
  page: 1,
};

function DepartmentSelect(props: {
  selected: SelectOption;
  onChange: (opt: SelectOption) => void;
}) {
  const loadOptions = async (
    search: string,
    _loadedOptions: unknown,
    { page }: AdditionalType,
  ) => {
    const result = await HrisDepartmentService.list({
      page: page,
      name: search,
    });

    return {
      options: result.hrisDepartments.map(
        (department) =>
          ({
            label: department.name,
            value: department.externalId,
          } as SelectOption),
      ),
      hasMore: !!result.nextPage,
      additional: {
        page: page + 1,
      },
    };
  };

  return (
    <CreatablePaginatedSelect
      additional={defaultAdditional}
      isClearable={true}
      value={props.selected}
      loadOptions={loadOptions}
      onChange={props.onChange}
      debounceTimeout={300}
      placeholder='Select Department'
      closeMenuOnSelect={true}
    />
  );
}

function JobTitleSelect(props: {
  currentValue: string;
  onChange: (label: string, isNew: boolean) => void;
}) {
  const value: SelectOption | null = props.currentValue
    ? { label: props.currentValue, value: props.currentValue }
    : null;

  const loadOptions = async (
    search: string,
    _loadedOptions: unknown,
    { page }: AdditionalType,
  ) => {
    const result = await HrisJobTitleService.list({
      page: page,
      name: search,
    });

    return {
      options: result.hrisJobTitles.map(
        (jobTitle) =>
          ({
            label: jobTitle.name,
            value: jobTitle.externalId ?? jobTitle.name,
          } as SelectOption),
      ),
      hasMore: !!result.nextPage,
      additional: {
        page: page + 1,
      },
    };
  };

  return (
    <CreatablePaginatedSelect
      additional={defaultAdditional}
      isClearable={true}
      value={value}
      loadOptions={loadOptions}
      onChange={(opt: SelectOption) => {
        props.onChange(opt?.label ?? '', isFreeText(opt));
      }}
      debounceTimeout={300}
      placeholder='Select or type a Job Title'
      closeMenuOnSelect={true}
    />
  );
}

export function JobAndDepartmentRow(props: PropTypes) {
  const [jobTitleIsFreeText, setJobTitleIsFreeText] = React.useState(false);
  const { dirtyFields } = useFormState({ control: props.control });
  const jobNameDirty = Boolean(dirtyFields.jobName);
  const departmentDirty = Boolean(dirtyFields.department);

  // Register jobName so react-hook-form tracks the value
  React.useEffect(() => {
    props.register('jobName', { required: true });
  }, [props.register]);

  return (
    <Row className='mb-3 align-items-start'>
      <Col sm={6}>
        <Controller
          name='jobName'
          control={props.control}
          rules={{ required: 'Job Title is required' }}
          render={({ field }) => {
            const currentLabel = field.value as unknown as string;
            return (
              <>
                <FormFieldHeader
                  fieldName={'Job Title'}
                  isRequired={true}
                  tooltipText={hrisFieldWarningTooltip({
                    currentLabel,
                    suggestionLabel: props.jobTitleSuggestionLabel,
                    freeText: jobTitleIsFreeText,
                    dirty: jobNameDirty,
                    fieldLabel: 'job title',
                  })}
                  tooltipVariant='warning'
                  tooltipPlaceholder={true}
                />
                <JobTitleSelect
                  currentValue={currentLabel}
                  onChange={(label, isNew) => {
                    field.onChange(label);
                    props.setValue('jobName', label);
                    setJobTitleIsFreeText(isNew);
                  }}
                />
              </>
            );
          }}
        />
      </Col>
      <Col sm={6}>
        <Controller
          name='department'
          control={props.control}
          render={({ field }) => {
            return (
              <>
                <FormFieldHeader
                  fieldName={'Department'}
                  isRequired={false}
                  tooltipText={hrisFieldWarningTooltip({
                    currentLabel: field.value?.label,
                    suggestionLabel: props.departmentSuggestionLabel,
                    freeText: isFreeText(field.value),
                    dirty: departmentDirty,
                    fieldLabel: 'department name',
                  })}
                  tooltipVariant='warning'
                  tooltipPlaceholder={true}
                />
                <DepartmentSelect
                  selected={field.value}
                  onChange={field.onChange}
                />
              </>
            );
          }}
        />
      </Col>
    </Row>
  );
}
